// SourceHighlightPanel.jsx -- NFI-5884: click a value on the review grid, see where it came from in the source PDF.
// A side panel that stays open and follows the clicked cell (the grid stays usable). Additive: the existing iframe viewer is
// untouched. A value that cannot be placed honestly shows a plain message and no box -- the review screen behaves as before.
// The page opens zoomed to the outlined spot so the number is readable on wide statements; zoom out / in / fit page width.
// A value printed more than once on its record's page (Current Period and YTD) opens that page with each place outlined, and
// every page that prints the value is listed (HAR-2326).
import React, { useEffect, useRef, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import { buildIndex, locate, wordsFromItems } from '../../../utils/valueLocator';
import { getPdfjs, OPEN_OPTIONS } from '../../../utils/pdfjsLoader';

export const PANEL_WIDTH = 600;
const RENDER_WIDTH = PANEL_WIDTH - 40;
const PAD_PT = 2.5; // glyph edges: pad the highlight a little
const CACHE_MAX = 2; // parsed documents kept for the life of the page; older ones are destroyed
const READ_PX_PER_PT = 1.4; // opening zoom: about a printed page at screen size, so a 9 pt figure is readable
const ZOOM_MIN = 1; // 1 = the page fits the panel's width
const ZOOM_MAX = 4;
const ZOOM_STEP = 1.25;
const MAX_CANVAS_PX = 16e6; // keep the canvas backing store bounded on large pages / high zoom

const cache = new Map(); // key -> Promise<{ pdf, index }>, oldest first

const yieldToBrowser = () => new Promise((resolve) => setTimeout(resolve, 0));
const warn = (what, err) => console.warn('[NFI-5884] source panel: ' + what + ':', err && err.name, err && err.message); // never the URL

/** A fresh presigned link for the file (the one the screen holds can expire while a reviewer works). */
export async function fetchPresignedUrl(fileInstanceId) {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileInstanceId}/presigned-url`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error(`presigned-url ${res.status}`);
  const data = await res.json();
  if (!data || !data.url) throw new Error('presigned-url returned no url');
  return data.url;
}

async function openPdf(url) {
  const pdfjsLib = await getPdfjs();
  return pdfjsLib.getDocument({ url, ...OPEN_OPTIONS }).promise;
}

/** Load + index a document once per key; on a failed first load, retry once with a fresh presigned link. */
export function loadDocument(key, url, fileInstanceId, onProgress) {
  if (cache.has(key)) {
    const hit = cache.get(key);
    cache.delete(key);
    cache.set(key, hit); // most recently used last
    return hit;
  }
  const promise = (async () => {
    let pdf;
    try {
      pdf = await openPdf(url);
    } catch (err) {
      if (!fileInstanceId) throw err;
      warn('first load failed, retrying with a fresh link', err);
      pdf = await openPdf(await fetchPresignedUrl(fileInstanceId));
    }
    const pages = [];
    for (let n = 1; n <= pdf.numPages; n++) {
      const page = await pdf.getPage(n);
      const viewport = page.getViewport({ scale: 1 });
      const tc = await page.getTextContent();
      pages.push({ number: n, width: viewport.width, height: viewport.height, words: wordsFromItems(tc.items, viewport) });
      if (onProgress) onProgress(n, pdf.numPages);
      if (n % 8 === 0) await yieldToBrowser(); // keep the review screen responsive on long documents
    }
    return { pdf, index: buildIndex(pages) };
  })();
  cache.set(key, promise);
  promise.catch((err) => { if (cache.get(key) === promise) cache.delete(key); warn('document not available', err); });
  while (cache.size > CACHE_MAX) {
    const [oldKey, oldPromise] = cache.entries().next().value;
    cache.delete(oldKey);
    oldPromise.then(({ pdf }) => pdf.destroy()).catch(() => {});
  }
  return promise;
}

export function clearSourceCache() {
  for (const p of cache.values()) p.then(({ pdf }) => pdf.destroy()).catch(() => {});
  cache.clear();
}

const NOTE = {
  rounded: (t) => `Printed as ${t} (rounded on the document).`,
  sign: (t) => `Printed as ${t} -- the document shows the opposite sign.`,
  percent: (t) => `Printed as ${t} (a percent on the document).`,
};

const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

/** The opening zoom for a page with an outlined spot: readable size, never smaller than fitting the panel's width. */
export function readableZoom(pageSize) {
  if (!pageSize || !pageSize[0]) return ZOOM_MIN;
  return clampZoom(Math.round((READ_PX_PER_PT * pageSize[0] / RENDER_WIDTH) * 100) / 100);
}

/** Every page the value is printed on, the record's own page first. */
export function pageList(prov) {
  if (!prov) return [];
  const pages = Array.isArray(prov.pages) && prov.pages.length
    ? prov.pages : Array.from(new Set((prov.candidates || []).map((c) => c.page))).sort((a, b) => a - b);
  const gp = Number.isInteger(prov.group_page) ? prov.group_page : null;
  return gp === null || !pages.includes(gp) ? pages : [gp, ...pages.filter((p) => p !== gp)];
}

function boxSx(b, padX, padY, dashed) {
  return {
    position: 'absolute',
    left: `${(b[0] - padX) * 100}%`,
    top: `${(b[1] - padY) * 100}%`,
    width: `${(b[2] - b[0] + 2 * padX) * 100}%`,
    height: `${(b[3] - b[1] + 2 * padY) * 100}%`,
    border: dashed ? '2px dashed #f59e0b' : '2px solid #f59e0b',
    backgroundColor: dashed ? 'transparent' : 'rgba(245, 158, 11, 0.18)',
    borderRadius: '2px',
    pointerEvents: 'none',
  };
}

export default function SourceHighlightPanel({ open, onClose, pdfUrl, fileInstanceId, fileName, query }) {
  const canvasRef = useRef(null);
  const [state, setState] = useState({ phase: 'idle' });
  const [pageNo, setPageNo] = useState(null);
  const [progress, setProgress] = useState(null);
  const [zoom, setZoom] = useState(null); // null = the opening zoom for the page shown
  const viewRef = useRef(null);
  const docKey = fileInstanceId ? 'file:' + fileInstanceId : pdfUrl;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    let cancelled = false;
    if (!open || !pdfUrl || !query) return undefined;
    setState({ phase: 'loading' });
    setPageNo(null);
    loadDocument(docKey, pdfUrl, fileInstanceId, (n, total) => { if (!cancelled) setProgress([n, total]); })
      .then(({ index }) => {
        if (cancelled) return;
        if (!index.textLayer) { setState({ phase: 'scan' }); return; }
        const items = query.rowItems && query.rowItems.length ? query.rowItems : [{ key: query.key, value: query.value }];
        const focus = query.rowItems && query.rowItems.length ? query.focusIndex : 0;
        const prov = locate(index, items, fileName || 'document.pdf')[focus].provenance;
        if (!prov) { setState({ phase: 'not_found' }); return; }
        if (prov.page) { setState({ phase: 'found', prov }); setPageNo(prov.page); return; }
        if (prov.match === 'ambiguous') {
          setState({ phase: 'ambiguous', prov });
          const pages = pageList(prov);
          setPageNo(pages.length ? pages[0] : null);
          return;
        }
        setState({ phase: 'not_found', prov });
      })
      .catch(() => { if (!cancelled) setState({ phase: 'error' }); });
    return () => { cancelled = true; };
  }, [open, pdfUrl, fileInstanceId, docKey, fileName, query]);

  useEffect(() => { setZoom(null); }, [pageNo, query]);

  const prov = state.prov;
  const found = state.phase === 'found' && prov && prov.bbox_norm && pageNo === prov.page ? prov : null;
  const shownCandidates = state.phase === 'ambiguous' && prov ? (prov.candidates || []).filter((c) => c.page === pageNo) : [];
  const sizeRef = found ? found.page_size : (shownCandidates[0] ? shownCandidates[0].page_size
    : (prov && prov.candidates && prov.candidates[0] ? prov.candidates[0].page_size : null));
  const focusBox = found ? found.bbox_norm : (shownCandidates[0] ? shownCandidates[0].bbox_norm : null);
  const z = zoom === null ? (focusBox ? readableZoom(sizeRef) : ZOOM_MIN) : zoom;
  const drawWidth = Math.round(RENDER_WIDTH * z);

  useEffect(() => {
    let cancelled = false;
    let task = null;
    if (!open || !pageNo || !pdfUrl) return undefined;
    loadDocument(docKey, pdfUrl, fileInstanceId).then(async ({ pdf }) => {
      if (cancelled || !canvasRef.current) return;
      const page = await pdf.getPage(pageNo);
      if (cancelled || !canvasRef.current) return;
      const base = page.getViewport({ scale: 1 });
      const dpr = Math.min(typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1, 2);
      let scale = (drawWidth / base.width) * dpr;
      const area = base.width * base.height * scale * scale;
      if (area > MAX_CANVAS_PX) scale *= Math.sqrt(MAX_CANVAS_PX / area);
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      task = page.render({ canvasContext: canvas.getContext('2d'), viewport });
      await task.promise;
    }).catch((err) => { if (!cancelled && err && err.name !== 'RenderingCancelledException') warn('page render failed', err); });
    return () => { cancelled = true; if (task) task.cancel(); };
  }, [open, pageNo, pdfUrl, fileInstanceId, docKey, drawWidth]);

  // bring the outlined spot to the middle of the view (the page's drawn height comes from its size, so no wait for the render)
  const drawHeight = sizeRef && sizeRef[0] ? Math.round(drawWidth * (sizeRef[1] / sizeRef[0])) : null;
  const scrollTarget = focusBox && drawHeight
    ? [Math.round(((focusBox[0] + focusBox[2]) / 2) * drawWidth), Math.round(((focusBox[1] + focusBox[3]) / 2) * drawHeight)] : null;
  const scrollKey = scrollTarget ? scrollTarget.join(',') : '';
  useEffect(() => {
    const el = viewRef.current;
    if (!el || !scrollTarget) return;
    el.scrollLeft = Math.max(0, scrollTarget[0] - el.clientWidth / 2);
    el.scrollTop = Math.max(0, scrollTarget[1] - el.clientHeight / 2);
  }, [scrollKey, pageNo]); // eslint-disable-line react-hooks/exhaustive-deps

  const padX = sizeRef ? PAD_PT / sizeRef[0] : 0;
  const padY = sizeRef ? PAD_PT / sizeRef[1] : 0;
  const note = found && NOTE[found.match] ? NOTE[found.match](found.printed_text) : null;
  const pages = state.phase === 'ambiguous' ? pageList(prov) : [];
  const groupPage = state.phase === 'ambiguous' && prov && Number.isInteger(prov.group_page) ? prov.group_page : null;
  const onGroupPage = groupPage !== null && pageNo === groupPage;

  return (
    <Drawer anchor="right" variant="persistent" open={!!open}
      PaperProps={{ sx: { width: PANEL_WIDTH, maxWidth: '95vw', boxSizing: 'border-box', p: 2, zIndex: (t) => t.zIndex.drawer + 2 } }}
      data-testid="source-panel">
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pr: 5, position: 'relative' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
            Source: {query ? String(query.key) : ''}{query && query.value !== undefined && query.value !== null ? ' = ' + String(query.value) : ''}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {fileName || ''}{pageNo ? (fileName ? ' -- ' : '') + 'page ' + pageNo : ''}
          </Typography>
        </Box>
        <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: -8, top: -8 }}><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ mt: 1.5 }}>
        {state.phase === 'loading' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }} data-testid="source-loading">
            <CircularProgress size={22} />
            <Typography variant="body2">
              {progress && progress[1] > 1 ? `Reading the document (page ${progress[0]} of ${progress[1]})...` : 'Finding the value in the document...'}
            </Typography>
          </Box>
        )}
        {state.phase === 'error' && (
          <Typography variant="body2" sx={{ py: 2 }} data-testid="source-unavailable">
            Source view not available for this document. The values and the document on the review screen are unchanged.
          </Typography>
        )}
        {state.phase === 'scan' && (
          <Typography variant="body2" sx={{ py: 2 }} data-testid="source-scan">
            This document is a scan (it has no text layer), so values can't be pinned on it yet.
          </Typography>
        )}
        {state.phase === 'not_found' && (
          <Typography variant="body2" sx={{ py: 2 }} data-testid="source-not-found">
            Couldn't find this value printed in the document (it may have been calculated or reworded during processing).
          </Typography>
        )}
        {note && (
          <Typography variant="body2" sx={{ py: 0.5, color: 'text.secondary' }} data-testid="source-note">{note}</Typography>
        )}
        {state.phase === 'ambiguous' && (
          <Box sx={{ py: 1 }} data-testid="source-ambiguous">
            {groupPage !== null ? (
              <Typography variant="body2" sx={{ mb: 1 }} data-testid="source-group-page">
                This record is on page {groupPage}. The value is printed there {prov.group_page_count} times (for example in more than
                one column), so each place is outlined. Every page with this value:
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ mb: 1 }}>
                This value appears in {prov.candidate_count} places on {pages.length} {pages.length === 1 ? 'page' : 'pages'}, so no single
                spot is picked. Each place on the page is outlined. Pages:
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 112, overflowY: 'auto' }} data-testid="source-pages">
              {pages.map((p) => (
                <Button key={p} size="small" variant={pageNo === p ? 'contained' : 'outlined'} onClick={() => setPageNo(p)}>
                  {p === groupPage ? `Page ${p} (this record)` : `Page ${p}`}
                </Button>
              ))}
            </Box>
          </Box>
        )}
        {pageNo ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }} data-testid="source-zoom">
              <Button size="small" aria-label="Zoom out" disabled={z <= ZOOM_MIN} onClick={() => setZoom(clampZoom(z / ZOOM_STEP))}>-</Button>
              <Typography variant="body2" sx={{ minWidth: 48, textAlign: 'center' }} data-testid="source-zoom-level">{Math.round(z * 100)}%</Typography>
              <Button size="small" aria-label="Zoom in" disabled={z >= ZOOM_MAX} onClick={() => setZoom(clampZoom(z * ZOOM_STEP))}>+</Button>
              <Button size="small" aria-label="Fit page width" onClick={() => setZoom(ZOOM_MIN)}>Fit page</Button>
            </Box>
            <Box ref={viewRef} data-testid="source-view" data-scroll-target={scrollKey}
              sx={{ width: RENDER_WIDTH, maxHeight: 'calc(100vh - 240px)', minHeight: 160, overflow: 'auto', mt: 0.5,
                border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Box sx={{ position: 'relative', width: drawWidth }}>
                <canvas ref={canvasRef} data-testid="source-canvas" style={{ display: 'block', width: drawWidth }} />
                {found ? <Box data-testid="source-highlight" sx={boxSx(found.bbox_norm, padX, padY, false)} /> : null}
                {shownCandidates.map((c, i) => <Box key={i} data-testid="source-candidate" sx={boxSx(c.bbox_norm, padX, padY, !onGroupPage)} />)}
              </Box>
            </Box>
          </>
        ) : null}
      </Box>
    </Drawer>
  );
}
